import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography
} from '@mui/material';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3002/api';

const emptyPlan = {
  plan_type: 'activation',
  name: '',
  description: '',
  price: '0',
  duration_days: '90',
  currency: 'INR',
  status: 'active',
  sort_order: '0'
};

const SchoolSubscriptionPlans = ({ schools = [] }) => {
  const [schoolId, setSchoolId] = useState('');
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingPlan, setEditingPlan] = useState(null);
  const [form, setForm] = useState(emptyPlan);
  const [saving, setSaving] = useState(false);

  const loadPlans = useCallback(async () => {
    if (!schoolId) {
      setPlans([]);
      return;
    }
    try {
      setLoading(true);
      setError('');
      const token = localStorage.getItem('authToken');
      const response = await fetch(
        `${API_BASE_URL}/admin/schools/${schoolId}/subscription-plans`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(data.message || 'Failed to load plans');
      }
      setPlans(Array.isArray(data.items) ? data.items : []);
    } catch (err) {
      setPlans([]);
      setError(err.message || 'Failed to load plans');
    } finally {
      setLoading(false);
    }
  }, [schoolId]);

  useEffect(() => {
    loadPlans();
  }, [loadPlans]);

  const openCreate = () => {
    setEditingPlan(null);
    setForm(emptyPlan);
    setDialogOpen(true);
  };

  const openEdit = (plan) => {
    setEditingPlan(plan);
    setForm({
      plan_type: plan.plan_type,
      name: plan.name || '',
      description: plan.description || '',
      price: String(plan.price ?? 0),
      duration_days: String(plan.duration_days ?? 90),
      currency: plan.currency || 'INR',
      status: plan.status || 'active',
      sort_order: String(plan.sort_order ?? 0)
    });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!schoolId) return;
    try {
      setSaving(true);
      setError('');
      const token = localStorage.getItem('authToken');
      const payload = {
        ...form,
        price: Number(form.price),
        duration_days: Number(form.duration_days),
        sort_order: Number(form.sort_order)
      };
      const url = editingPlan
        ? `${API_BASE_URL}/admin/schools/${schoolId}/subscription-plans/${editingPlan.id}`
        : `${API_BASE_URL}/admin/schools/${schoolId}/subscription-plans`;
      const response = await fetch(url, {
        method: editingPlan ? 'PUT' : 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(data.message || 'Failed to save plan');
      }
      setDialogOpen(false);
      await loadPlans();
    } catch (err) {
      setError(err.message || 'Failed to save plan');
    } finally {
      setSaving(false);
    }
  };

  const handleSeed = async () => {
    if (!schoolId) return;
    try {
      setSaving(true);
      setError('');
      const token = localStorage.getItem('authToken');
      const response = await fetch(
        `${API_BASE_URL}/admin/schools/${schoolId}/subscription-plans/seed`,
        {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(data.message || 'Seed failed');
      }
      setPlans(Array.isArray(data.items) ? data.items : []);
    } catch (err) {
      setError(err.message || 'Seed failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box>
      <Typography variant="h5" gutterBottom>
        School Subscription Plans
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Per-school activation and renewal plans. Duration is always in days.
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      <Card sx={{ p: 2, mb: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={6}>
            <FormControl fullWidth size="small">
              <InputLabel>School</InputLabel>
              <Select
                label="School"
                value={schoolId}
                onChange={(e) => setSchoolId(e.target.value)}
              >
                {schools.map((s) => (
                  <MenuItem key={s.id} value={String(s.id)}>
                    {s.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={6}>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              <Button variant="contained" onClick={openCreate} disabled={!schoolId}>
                Add Plan
              </Button>
              <Button variant="outlined" onClick={handleSeed} disabled={!schoolId || saving}>
                Seed from global defaults
              </Button>
              <Button variant="text" onClick={loadPlans} disabled={!schoolId || loading}>
                Refresh
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Card>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Type</TableCell>
              <TableCell>Name</TableCell>
              <TableCell>Price (₹)</TableCell>
              <TableCell>Days</TableCell>
              <TableCell>Status</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {plans.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  {schoolId ? 'No plans yet. Seed defaults or add a plan.' : 'Select a school.'}
                </TableCell>
              </TableRow>
            ) : (
              plans.map((plan) => (
                <TableRow key={plan.id}>
                  <TableCell>{plan.plan_type}</TableCell>
                  <TableCell>{plan.name}</TableCell>
                  <TableCell>{plan.price}</TableCell>
                  <TableCell>{plan.duration_days}</TableCell>
                  <TableCell>{plan.status}</TableCell>
                  <TableCell align="right">
                    <Button size="small" onClick={() => openEdit(plan)}>
                      Edit
                    </Button>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      )}

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editingPlan ? 'Edit Plan' : 'Add Plan'}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth size="small">
                <InputLabel>Plan type</InputLabel>
                <Select
                  label="Plan type"
                  value={form.plan_type}
                  onChange={(e) => setForm((p) => ({ ...p, plan_type: e.target.value }))}
                >
                  <MenuItem value="activation">Activation</MenuItem>
                  <MenuItem value="renewal">Renewal</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                size="small"
                label="Duration (days)"
                type="number"
                value={form.duration_days}
                onChange={(e) => setForm((p) => ({ ...p, duration_days: e.target.value }))}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                size="small"
                label="Name"
                value={form.name}
                onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                size="small"
                label="Description"
                value={form.description}
                onChange={(e) => setForm((p) => ({ ...p, description: e.target.value }))}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                size="small"
                label="Price"
                type="number"
                value={form.price}
                onChange={(e) => setForm((p) => ({ ...p, price: e.target.value }))}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                size="small"
                label="Currency"
                value={form.currency}
                onChange={(e) => setForm((p) => ({ ...p, currency: e.target.value }))}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth size="small">
                <InputLabel>Status</InputLabel>
                <Select
                  label="Status"
                  value={form.status}
                  onChange={(e) => setForm((p) => ({ ...p, status: e.target.value }))}
                >
                  <MenuItem value="active">Active</MenuItem>
                  <MenuItem value="inactive">Inactive</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSave} disabled={saving || !form.name}>
            {saving ? 'Saving…' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SchoolSubscriptionPlans;
